// apps/account-portal/src/pages/BoardPage.tsx
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ConfirmModal, ListPagination, btnPrimary, type StatusTone } from '@redpanda/ui';
import { ChatBubbleLeftIcon, HandThumbUpIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { HandThumbUpIcon as HandThumbUpSolidIcon } from '@heroicons/react/24/solid';
import { boardApi } from '../services/api';
import { useBoardStore, BoardPost, BoardComment, POST_TYPES } from '../stores/boardStore';
import { UserContext } from '../context/UserContext';
import PageFooter from '../components/PageFooter';
import PageContainer from '../components/PageContainer';
import FeedToolbar from '../components/feedback/FeedToolbar';
import PostDialog, { PostDraft } from '../components/feedback/PostDialog';
import OfficialReply from '../components/feedback/OfficialReply';
import PostHeading from '../components/feedback/PostHeading';
import { formatDateTime } from '../components/feedback/feedbackFormat';

// 色點：Bug 紅色提醒、意見回饋品牌色、一般灰色（與許願清單的狀態色點同一個樣式）
const TYPE_TONE: Record<string, StatusTone> = { BUG: 'danger', FEEDBACK: 'accent', GENERAL: 'neutral' };
const COMMENT_MAX = 1000;

const iconButton = 'rounded p-1 text-fg-muted transition-colors';

/** 一則留言底下的回覆：點「回覆」才載入；訪客也能回覆，編輯只限登入的本人 */
const Comments: React.FC<{ post: BoardPost; myId?: string | null; onCountChange: (delta: number) => void }> = ({ post, myId, onCountChange }) => {
  const { t } = useTranslation();
  const [comments, setComments] = useState<BoardComment[] | null>(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    boardApi.getComments(post.id)
      .then(res => setComments(Array.isArray(res.data) ? res.data : []))
      .catch(() => { setComments([]); toast.error(t('pages.board.comments_load_error')); });
  }, [post.id, t]);
  useEffect(() => { if (comments?.length === 0) inputRef.current?.focus(); }, [comments]);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const res = await boardApi.createComment(post.id, { content: text.trim() });
      setComments(prev => [...(prev ?? []), res.data]);
      setText('');
      onCountChange(1);
    } catch (err: any) {
      toast.error(err.response?.status === 429 ? t('pages.feedback.rate_limited') : t('pages.board.reply_error'));
    } finally {
      setSending(false);
    }
  };

  const saveEdit = async () => {
    if (!editing?.text.trim()) return;
    try {
      const res = await boardApi.updateComment(post.id, editing.id, { content: editing.text.trim() });
      setComments(prev => prev?.map(c => (c.id === editing.id ? res.data : c)) ?? null);
      setEditing(null);
    } catch {
      toast.error(t('pages.board.comment_edit_error'));
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    const id = deleteId;
    setDeleteId(null);
    try {
      await boardApi.deleteComment(post.id, id);
      setComments(prev => prev?.filter(c => c.id !== id) ?? null);
      onCountChange(-1);
    } catch {
      toast.error(t('pages.board.comment_delete_error'));
    }
  };

  return (
    <div className="mt-3 space-y-2 border-t border-line pt-3">
      {comments === null ? (
        <p className="text-sm text-fg-muted">{t('common.status.loading')}</p>
      ) : comments.length === 0 ? (
        <p className="text-sm text-fg-muted">{t('pages.board.no_replies')}</p>
      ) : comments.map(comment => (
        <div key={comment.id} className="rounded-md bg-slate-50 px-3 py-2 dark:bg-slate-800/60">
          <div className="flex flex-wrap items-center justify-between gap-2 text-[13px]">
            <span className="font-medium text-fg">{comment.authorName}</span>
            <span className="flex items-center gap-1 text-fg-muted">
              <span className="tabular-nums">{formatDateTime(comment.createdAt)}</span>
              {comment.isEdited && <span>· {t('pages.board.comment_edited')}</span>}
              {!!myId && comment.isMine && editing?.id !== comment.id && (
                <>
                  <button type="button" onClick={() => setEditing({ id: comment.id, text: comment.content })} className={`${iconButton} hover:text-accent`} aria-label={t('pages.board.comment_edit')}>
                    <PencilSquareIcon className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onClick={() => setDeleteId(comment.id)} className={`${iconButton} hover:text-red-600`} aria-label={t('pages.board.delete')}>
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </span>
          </div>
          {editing?.id === comment.id ? (
            <div className="mt-2 flex gap-2">
              <input
                value={editing.text}
                maxLength={COMMENT_MAX}
                onChange={e => setEditing({ id: comment.id, text: e.target.value })}
                onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditing(null); }}
                className="h-9 flex-1 rounded-md border border-line bg-surface px-3 text-sm text-fg"
                autoFocus
              />
              <button type="button" onClick={() => setEditing(null)} className="text-sm text-fg-secondary hover:text-fg">{t('pages.board.comment_cancel_edit')}</button>
              <button type="button" onClick={saveEdit} className="text-sm font-medium text-accent hover:underline">{t('pages.board.comment_save_edit')}</button>
            </div>
          ) : (
            <p className="mt-1 text-sm text-fg-secondary whitespace-pre-wrap break-words">{comment.content}</p>
          )}
        </div>
      ))}

      <form onSubmit={send} className="flex gap-2 pt-1">
        <input
          ref={inputRef}
          value={text}
          maxLength={COMMENT_MAX}
          onChange={e => setText(e.target.value)}
          placeholder={t('pages.board.reply_placeholder')}
          aria-label={t('pages.board.reply_placeholder')}
          className="h-10 flex-1 rounded-md border border-line bg-surface px-3 text-sm text-fg placeholder:text-fg-muted"
        />
        <button type="submit" disabled={sending || !text.trim()} className={btnPrimary}>
          {sending ? t('pages.board.reply_submitting') : t('pages.board.reply_submit')}
        </button>
      </form>

      <ConfirmModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={confirmDelete}
        title={t('pages.board.delete_comment_title')}
        message={t('pages.board.delete_comment_message')}
        confirmText={t('pages.board.delete')}
        cancelText={t('pages.board.cancel')}
        type="danger"
      />
    </div>
  );
};

/**
 * 留言專區：一般留言、意見回饋、Bug 回報；置頂在前、新的在前
 * 訪客也能留言、回覆、按讚；編輯只限登入的本人；「小熊貓科技」官方回覆由營運後台發
 */
const BoardPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, cachedUserId } = useContext(UserContext)!;
  const { data, query, isFetching, error, fetch, open, patch, prepend, remove } = useBoardStore();
  const [dialog, setDialog] = useState<{ post?: BoardPost } | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [likingId, setLikingId] = useState<string | null>(null);
  const [openComments, setOpenComments] = useState<Set<string>>(new Set());

  useEffect(() => { open(); }, [open]);
  useEffect(() => { if (error) toast.error(t('pages.board.error_load')); }, [error, t]);

  const myId = user?.id ?? cachedUserId;
  const isMine = (post: BoardPost) => !!myId && post.isMine;
  const typeLabel = (type: string) => t(`pages.board.post_type.${type}`);

  const handleLike = async (post: BoardPost) => {
    if (likingId) return;
    setLikingId(post.id);
    const liked = !post.userHasLiked;
    patch(post.id, { userHasLiked: liked, likeCount: post.likeCount + (liked ? 1 : -1) }); // 樂觀更新
    try {
      const res = liked ? await boardApi.likePost(post.id) : await boardApi.unlikePost(post.id);
      patch(post.id, res.data);
    } catch {
      patch(post.id, { userHasLiked: post.userHasLiked, likeCount: post.likeCount });
      toast.error(t('pages.feedback.action_failed'));
    } finally {
      setLikingId(null);
    }
  };

  const toggleComments = (id: string) => setOpenComments(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const handleSubmit = async (draft: PostDraft) => {
    const payload = { postType: draft.postType || 'GENERAL', title: draft.title, content: draft.content };
    try {
      if (dialog?.post) {
        const res = await boardApi.updatePost(dialog.post.id, payload);
        patch(dialog.post.id, { postType: res.data.postType, title: res.data.title, content: res.data.content });
        toast.success(t('pages.board.update_success'));
      } else {
        const res = await boardApi.createPost(payload);
        prepend(res.data);
        toast.success(t('pages.board.success_submit'));
      }
      setDialog(null);
    } catch (err: any) {
      toast.error(err.response?.status === 429 ? t('pages.feedback.rate_limited') : t('pages.board.error_submit'));
      throw err;
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const id = deleteId;
    setDeleteId(null);
    remove(id);
    try {
      await boardApi.deletePost(id);
      fetch(); // 更新各篩選筆數；這頁刪空了會自動跳到最後一頁
    } catch {
      toast.error(t('pages.board.delete_error'));
      fetch();
    }
  };

  const posts = data?.posts ?? [];
  // 分頁（照 CRM：列表上下各一個 ListPagination，只有一頁時不顯示）
  const pagination = data && { currentPage: data.page, totalPages: data.totalPages, totalItems: data.total, pageSize: data.pageSize };
  const changePage = (page: number) => {
    fetch({ page });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const filters = [
    { key: null, label: t('pages.board.filter_all'), count: data ? POST_TYPES.reduce((sum, type) => sum + (data.counts[type] ?? 0), 0) : undefined },
    ...POST_TYPES.map(type => ({ key: type, label: typeLabel(type), count: data?.counts[type] })),
  ];

  return (
    <PageContainer>
      {/* 標題區：與其他頁面相同位置 */}
      <div className="mb-4">
        <h1 className="text-2xl font-semibold text-fg mb-2">{t('pages.board.title')}</h1>
        <p className="text-fg-secondary">{t('pages.board.description')}</p>
      </div>

      {/* 以下內容置中限寬（閱讀型的留言、許願列表） */}
      <div className="max-w-[720px] mx-auto">
        <FeedToolbar
          filters={filters}
          active={query.filter}
          onFilter={filter => fetch({ filter, page: 1 })}
          onSearch={q => fetch({ q, page: 1 })}
          searchPlaceholder={t('pages.board.search_placeholder')}
          action={<button type="button" onClick={() => setDialog({})} className={`${btnPrimary} shrink-0`}>{t('pages.board.new_post')}</button>}
        />

        <ListPagination pagination={pagination} onPageChange={changePage} isLoading={isFetching} className="mb-3" />
        {!data ? (
          <div className="rounded-lg border border-line bg-surface divide-y divide-line" aria-busy="true">
            {[1, 2, 3].map(i => (
              <div key={i} className="space-y-2 p-5 animate-pulse motion-reduce:animate-none">
                <div className="h-4 w-1/3 rounded bg-slate-200 dark:bg-slate-700" />
                <div className="h-4 w-full rounded bg-slate-200 dark:bg-slate-700" />
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="rounded-lg border border-line bg-surface p-12 text-center text-fg-secondary">
            {query.filter || query.q ? t('pages.feedback.empty_filtered') : t('pages.board.empty')}
          </div>
        ) : (
          <ul className={`rounded-lg border border-line bg-surface divide-y divide-line transition-opacity ${isFetching ? 'opacity-60' : ''}`}>
            {posts.map(post => (
              <li key={post.id} className="p-5">
                <PostHeading title={post.title} label={typeLabel(post.postType)} tone={TYPE_TONE[post.postType]} pinned={post.isPinned} />
                <p className="mt-1.5 text-sm text-fg-secondary whitespace-pre-wrap break-words leading-relaxed">{post.content}</p>
                {post.adminReply && <OfficialReply reply={post.adminReply} repliedAt={post.adminReplyAt} />}

                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-fg-muted">
                  <span>{post.authorName}<span className="mx-1.5" aria-hidden="true">·</span><span className="tabular-nums">{formatDateTime(post.createdAt)}</span></span>
                  <button
                    type="button"
                    onClick={() => handleLike(post)}
                    disabled={likingId === post.id}
                    aria-pressed={post.userHasLiked}
                    className={`inline-flex items-center gap-1 transition-colors ${post.userHasLiked ? 'text-accent' : 'hover:text-accent'}`}
                  >
                    {post.userHasLiked ? <HandThumbUpSolidIcon className="h-4 w-4" /> : <HandThumbUpIcon className="h-4 w-4" />}
                    <span className="tabular-nums">{post.likeCount}</span>
                  </button>
                  <button type="button" onClick={() => toggleComments(post.id)} aria-expanded={openComments.has(post.id)} className="inline-flex items-center gap-1 hover:text-accent">
                    <ChatBubbleLeftIcon className="h-4 w-4" />
                    {openComments.has(post.id)
                      ? t('pages.board.hide_replies')
                      : post.commentCount > 0 ? t('pages.board.view_replies', { count: post.commentCount }) : t('pages.board.reply')}
                  </button>
                  {isMine(post) && (
                    <span className="ml-auto flex items-center gap-1">
                      <button type="button" onClick={() => setDialog({ post })} className={`${iconButton} hover:text-accent`} aria-label={t('pages.board.edit')} title={t('pages.board.edit')}>
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button type="button" onClick={() => setDeleteId(post.id)} className={`${iconButton} hover:text-red-600 dark:hover:text-red-400`} aria-label={t('pages.board.delete')} title={t('pages.board.delete')}>
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </span>
                  )}
                </div>

                {openComments.has(post.id) && (
                  <Comments post={post} myId={myId} onCountChange={delta => patch(post.id, { commentCount: post.commentCount + delta })} />
                )}
              </li>
            ))}
          </ul>
        )}

        <ListPagination pagination={pagination} onPageChange={changePage} isLoading={isFetching} className="mt-6" />
      </div>

      {dialog && (
        <PostDialog
          title={t(dialog.post ? 'pages.board.edit_title' : 'pages.board.new_post')}
          initial={dialog.post && { title: dialog.post.title, content: dialog.post.content, postType: dialog.post.postType }}
          typeOptions={POST_TYPES.map(type => ({ value: type, label: typeLabel(type) }))}
          titlePlaceholder={t('pages.board.title_placeholder')}
          contentPlaceholder={t('pages.board.content_placeholder')}
          authorName={user ? (user.name || t('pages.feedback.member')) : t('pages.feedback.guest')}
          onSubmit={handleSubmit}
          onClose={() => setDialog(null)}
        />
      )}

      <ConfirmModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title={t('pages.board.delete_title')}
        message={t('pages.board.delete_message')}
        confirmText={t('pages.board.delete')}
        cancelText={t('pages.board.cancel')}
        type="danger"
      />

      <PageFooter />
    </PageContainer>
  );
};

export default BoardPage;
